import { useEffect, useState } from 'react';
import { getAllowedCommands } from '../Knowledge/KnowledgeUtils';
import { getUserName } from '../Utils/GeneralUtils';
import { isMockMode } from './mockTasks';

/**
 * One `get_allowed_commands` read per signed-in trainer, shared by every hook
 * user. The task list and the HT Center both ask, StrictMode mounts each twice,
 * and the answer cannot change mid-session — so the in-flight promise is what
 * is cached, not just its result. A failed read is forgotten so the next mount
 * retries rather than pinning the override to false for the session.
 */
const overrideByUser = new Map();

const readOverride = (user) => {
    if (!overrideByUser.has(user)) {
        overrideByUser.set(
            user,
            getAllowedCommands()
                .then((commands) => commands.includes('get_admin_overview'))
                .catch(() => {
                    overrideByUser.delete(user);
                    return false;
                })
        );
    }
    return overrideByUser.get(user);
};

export const resetCloseWalkOverride = () => overrideByUser.clear();

/**
 * Whether this operator still checks close tasks off inline once the close
 * window is open. Managers and Admins do — they fix and audit closes they were
 * never on the walk for — while a trainer's Done hands off to the guided walk.
 *
 * Same allowed-commands read the rest of the app gates manager UI on
 * (`get_admin_overview`, as `useAdminPermissions` does); the backend is still
 * the boundary. Defaults to false so a slow read hands off rather than quietly
 * granting the override, and mock mode answers "trainer" — unlike the admin
 * hooks — because the handoff is the behaviour worth exercising offline.
 */
export const useCloseWalkOverride = () => {
    const [canCompleteInline, setCanCompleteInline] = useState(false);

    useEffect(() => {
        if (isMockMode()) return undefined;
        let active = true;
        readOverride(getUserName() || '').then((allowed) => {
            if (active) setCanCompleteInline(allowed);
        });
        return () => {
            active = false;
        };
    }, []);

    return canCompleteInline;
};
